/**
 * Functions for fetching browse page data from the Peacock API
 */

import {
  RailWithTiles,
  FetchBrowsePageOptions,
  isGameRail,
  isSkippedRail,
} from "./types";

import { fetchAssetDetails } from "@/utils/FetchAssetDetails";
import {
  isCollection,
  isLink,
  AssetResponse,
} from "@/utils/FetchAssetDetails/types";
import { fetchChildItems } from "@/utils/FetchChildItems";
import { PeacockNodeType } from "@/utils/shared-atom-types";

/**
 * Fetches a complete browse page with all rails and their tiles
 *
 * @remarks
 * This function orchestrates the entire flow:
 * 1. Fetches the browse page group (e.g., "/home")
 * 2. Fetches all collections (rails) within that group
 * 3. For each collection, fetches the items (tiles)
 * 4. Optionally fetches full asset details for all tiles in each rail
 *
 * Note: CATALOGUE/LINK rails and CATALOGUE/COLLECTION rails with linkId are personalized content.
 * By default, these are skipped, but you can set skipPersonalized: false to attempt fetching them.
 * Personalized rails will be marked with isPersonalized: true in the response.
 *
 * @param slug - The browse page slug (e.g., "/home", "/movies")
 * @param options - Configuration options for fetching tile details and handling personalized rails
 * @returns Promise that resolves to an array of rails with their tiles
 *
 * @example
 * ```typescript
 * // Fetch home page with basic tile data
 * const rails = await fetchBrowsePage("/home");
 * console.log(`Found ${rails.length} rails`);
 *
 * // Fetch with full asset details for all tiles, including personalized
 * const railsWithDetails = await fetchBrowsePage("/home", {
 *   fetchTileDetails: true,
 *   skipPersonalized: false
 * });
 * ```
 *
 * @throws Will throw an error if the network request fails
 */
const fetchBrowsePage = async (
  slug: string,
  options?: FetchBrowsePageOptions,
): Promise<RailWithTiles[]> => {
  const {
    fetchTileDetails = false,
    skipPersonalized = true,
    showMobileContent = true,
  } = options || {};

  /** Step 1: Fetch the browse page group **/
  const browsePage = await fetchAssetDetails(slug);

  if (browsePage.type !== PeacockNodeType.Group) {
    throw new Error(
      `Expected ${PeacockNodeType.Group} but got ${browsePage.type} for ${slug}`,
    );
  }

  /** Step 2: Fetch all collections (rails) on this page **/
  const railsResponse = await fetchChildItems(browsePage.id);
  const rails = railsResponse.items;

  /** Step 3: For each rail, fetch its items (tiles) **/
  const railsWithTiles = await Promise.all(
    rails.map(async (rail) => {
      const railType = rail.type;
      const railId = rail.id;
      const title = rail.attributes?.title || "Untitled Rail";
      const railSlug = rail.attributes?.slug || "";
      const itemsCount = rail.childTypes?.items?.count || 0;

      /** Get renderHint and linkId - these exist on CollectionResponse and LinkResponse **/
      const renderHint =
        isCollection(rail) || isLink(rail)
          ? rail.attributes.renderHint
          : undefined;
      const hasLinkId =
        isCollection(rail) || isLink(rail) ? rail.attributes.linkId : undefined;
      const isPersonalized =
        railType === PeacockNodeType.Link ||
        (railType === PeacockNodeType.Collection && hasLinkId);

      if (isPersonalized && skipPersonalized) {
        return {
          ...rail,
          title,
          slug: railSlug,
          itemsCount,
          tiles: [],
          renderHint: renderHint || {},
          skipped: true,
          reason: "personalized",
          linkId: hasLinkId,
        } as RailWithTiles;
      }

      /** Handle CATALOGUE/COLLECTION - fetch items (including personalized ones) **/
      /** Handle CATALOGUE/LINK - also try to fetch items **/
      if (
        railType === PeacockNodeType.Collection ||
        railType === PeacockNodeType.Link
      ) {
        try {
          const tilesResponse = await fetchChildItems(rail);
          let tiles = tilesResponse.items;

          // Optionally fetch full asset details for all tiles
          let tilesWithDetails: AssetResponse[] = [];
          if (fetchTileDetails && tiles.length > 0) {
            /** Batch process tiles to avoid overwhelming the browser with concurrent requests **/
            const BATCH_SIZE = 10; /** Process 10 tiles at a time **/
            tilesWithDetails = [];

            for (let i = 0; i < tiles.length; i += BATCH_SIZE) {
              const batch = tiles.slice(i, i + BATCH_SIZE);
              const batchResults = await Promise.all(
                batch.map(async (tile) => {
                  try {
                    const tileWithImageType = tile as typeof tile & {
                      imageType?: string;
                    };

                    /** Handle CATALOGUE/LINK tiles - fetch the linked node for full details **/
                    if (tile.type === PeacockNodeType.Link) {
                      const linkDetails = await fetchAssetDetails(tile.id);

                      /** Preserve imageType if it was set **/
                      return tileWithImageType.imageType
                        ? {
                            ...linkDetails,
                            imageType: tileWithImageType.imageType,
                          }
                        : linkDetails;
                    }

                    /** Handle CATALOGUE/COLLECTION tiles - fetch full details including images **/
                    if (tile.type === PeacockNodeType.Collection) {
                      const collectionDetails = await fetchAssetDetails(
                        tile.id,
                      );
                      /** Preserve imageType if it was set **/
                      return tileWithImageType.imageType
                        ? {
                            ...collectionDetails,
                            imageType: tileWithImageType.imageType,
                          }
                        : collectionDetails;
                    }

                    /** Fetch details for assets, series, seasons, and episodes **/
                    if (
                      tile.type === PeacockNodeType.Programme ||
                      tile.type === PeacockNodeType.Series ||
                      tile.type === PeacockNodeType.Season ||
                      tile.type === PeacockNodeType.Episode ||
                      tile.type === PeacockNodeType.SLE
                    ) {
                      const details = await fetchAssetDetails(tile.id);
                      /** Preserve imageType if it was set **/
                      return tileWithImageType.imageType
                        ? { ...details, imageType: tileWithImageType.imageType }
                        : details;
                    }
                    /** For other types, return as-is **/
                    return tile;
                  } catch (error) {
                    /** Return original tile when fetch fails **/
                    return tile;
                  }
                }),
              );
              tilesWithDetails.push(...batchResults);
            }
          }

          return {
            ...rail,
            title,
            slug: railSlug,
            itemsCount,
            tiles,
            tilesWithDetails:
              tilesWithDetails.length > 0 ? tilesWithDetails : undefined,
            renderHint: renderHint || {},
            isPersonalized /** Mark if this is personalized content **/,
            linkId: hasLinkId || undefined,
          } as RailWithTiles;
        } catch (error) {
          return {
            ...rail,
            title,
            slug: railSlug,
            itemsCount,
            tiles: [],
            renderHint: renderHint || {},
            isPersonalized,
            linkId: hasLinkId || undefined,
            error: error instanceof Error ? error.message : String(error),
          } as RailWithTiles;
        }
      }

      /** Unknown rail type **/
      return {
        ...rail,
        title,
        slug: railSlug,
        itemsCount,
        tiles: [],
        renderHint: renderHint || {},
      } as RailWithTiles;
    }),
  );

  /** Filter rails based on options **/
  const filteredRails = railsWithTiles.filter((rail) => {
    /** Filter out personalized rails if skipPersonalized is true and rail is marked as skipped */
    if (
      skipPersonalized &&
      isSkippedRail(rail) &&
      rail.reason === "personalized"
    ) {
      return false;
    }

    /** Filter out mobile content if needed */
    if (!showMobileContent) {
      const contentLevel = rail.contentLevel;
      if (
        (contentLevel &&
          typeof contentLevel === "string" &&
          (contentLevel.includes("CLIP") || contentLevel.includes("STORY"))) ||
        isGameRail(rail)
      ) {
        return false;
      }
    }

    return true;
  });

  return filteredRails;
};

export { fetchBrowsePage };
